import type { ActiveEvent } from '@/types/dynamicEvents';

class EventStore {
  private listeners: Set<(
    eventHistory: ActiveEvent[],
    systemicNotifications: ActiveEvent[],
    activeEvents: ActiveEvent[],
    pendingReactiveEvents: ActiveEvent[]
  ) => void> = new Set();
  private eventHistory: ActiveEvent[] = [];
  private systemicNotifications: ActiveEvent[] = [];
  private activeEvents: ActiveEvent[] = [];

  addEvent(event: ActiveEvent) {
    console.log('[EventStore Service] Adding event:', event.event.title, 'type:', event.event.type, 'status:', event.status);

    // Ensure triggeredAt is always a Date object
    if (!event.triggeredAt || !(event.triggeredAt instanceof Date)) {
      event.triggeredAt = new Date();
    }

    // Check for duplicate events - same event ID and product context within a short time window
    const isDuplicate = this.isDuplicateEvent(event);
    if (isDuplicate) {
      console.log('[EventStore Service] Skipping duplicate event', event.event.id, 'for product', event.productContext?.productId);
      return;
    }

    if (event.event.type === 'reactive') {
      this.activeEvents = [...this.activeEvents, event];
      console.log('[EventStore Service] Added to activeEvents, count:', this.activeEvents.length);
    } else {
      this.systemicNotifications = [...this.systemicNotifications, event];
    }

    this.eventHistory = [...this.eventHistory, event];
    this.notifyListeners();
  }
  
  private isDuplicateEvent(newEvent: ActiveEvent): boolean {
    // Check if this is a phase-related event
    if (!newEvent.productContext) {
      return false;
    }
    
    // Look for duplicate events in recent history (last 5 seconds)
    const recentTimeWindow = 5000; // 5 seconds
    const now = new Date().getTime();
    
    // Check in event history
    const hasDuplicateInHistory = this.eventHistory.some(existingEvent => {
      if (!existingEvent.productContext) {
        return false;
      }
      
      // Handle cases where triggeredAt might be missing (though it shouldn't be after our fixes)
      const eventTime = existingEvent.triggeredAt instanceof Date ? existingEvent.triggeredAt.getTime() : 0;
      const timeDiff = now - eventTime;
      
      return (
        existingEvent.event.id === newEvent.event.id &&
        existingEvent.productContext.productId === newEvent.productContext.productId &&
        existingEvent.productContext.phase === newEvent.productContext.phase &&
        timeDiff < recentTimeWindow
      );
    });
    
    // Also check in active events to prevent duplicate pending events
    const hasDuplicateInActive = this.activeEvents.some(existingEvent => {
      return (
        existingEvent.event.id === newEvent.event.id &&
        existingEvent.productContext?.productId === newEvent.productContext.productId &&
        existingEvent.productContext?.phase === newEvent.productContext.phase &&
        existingEvent.status === 'pending'
      );
    });
    
    return hasDuplicateInHistory || hasDuplicateInActive;
  }

  dismissNotification(eventId: string) {
    this.systemicNotifications = this.systemicNotifications.filter(e => e.event.id !== eventId);
    this.notifyListeners();
  }

  handlePlayerChoice(eventId: string) {
    this.activeEvents = this.activeEvents.filter(e => e.event.id !== eventId);
    this.notifyListeners();
  }

  updateEventWithResolution(resolvedEvent: ActiveEvent) {
    // Update the event in history with the resolution details
    const historyIndex = this.eventHistory.findIndex(e => e.event.id === resolvedEvent.event.id);
    if (historyIndex !== -1) {
      this.eventHistory[historyIndex] = { ...resolvedEvent };
      this.notifyListeners();
    }
  }

  reset() {
    this.eventHistory = [];
    this.systemicNotifications = [];
    this.activeEvents = [];
    this.notifyListeners();
  }

  restoreEventHistory(history: Array<Omit<ActiveEvent, 'triggeredAt' | 'resolvedAt'> & { 
    triggeredAt?: string | Date; 
    resolvedAt?: string | Date;
  }>) {
    // console.log('EventStore: Restoring event history with', history.length, 'events');
    this.eventHistory = history.map(event => ({
      ...event,
      triggeredAt: event.triggeredAt ? new Date(event.triggeredAt) : new Date(),
      resolvedAt: event.resolvedAt ? new Date(event.resolvedAt) : undefined
    })) as ActiveEvent[];
    this.notifyListeners();
  }

  subscribe(callback: (
    eventHistory: ActiveEvent[],
    systemicNotifications: ActiveEvent[],
    activeEvents: ActiveEvent[],
    pendingReactiveEvents: ActiveEvent[]
  ) => void) {
    // console.log('EventStore: New subscriber, current history:', this.eventHistory.length);
    this.listeners.add(callback);
    // Immediately call with current state including all arrays
    const pending = this.activeEvents.filter(e =>
      e.event.type === 'reactive' && e.status === 'pending'
    );
    callback(this.eventHistory, this.systemicNotifications, this.activeEvents, pending);

    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners() {
    const pending = this.activeEvents.filter(e =>
      e.event.type === 'reactive' && e.status === 'pending'
    );
    console.log('[EventStore Service] notifyListeners - activeEvents:', this.activeEvents.length, 'pending:', pending.length, 'listeners:', this.listeners.size);
    this.listeners.forEach(listener => {
      listener(this.eventHistory, this.systemicNotifications, this.activeEvents, pending);
    });
  }

  getState() {
    return {
      eventHistory: this.eventHistory,
      systemicNotifications: this.systemicNotifications,
      activeEvents: this.activeEvents,
      pendingReactiveEvents: this.activeEvents.filter(e => 
        e.event.type === 'reactive' && e.status === 'pending'
      )
    };
  }

  getEventHistory() {
    return this.eventHistory;
  }
}

export const eventStore = new EventStore();